/**
 * How many lines a picture's caption takes on the sheet — so a plate can
 * budget the room under its rows before react-pdf sets the text. The PDF sets
 * captions in Helvetica; these are its advance widths from the standard AFM,
 * in thousandths of the font size.
 *
 * The count only plans the plate's height: the PDF flows every caption at its
 * full length, so an estimate that is off costs a little space, never words.
 */

const HELVETICA_WIDTHS: Record<string, number> = {
    ' ': 278,
    '!': 278,
    '"': 355,
    '#': 556,
    $: 556,
    '%': 889,
    '&': 667,
    "'": 191,
    '(': 333,
    ')': 333,
    '*': 389,
    '+': 584,
    ',': 278,
    '-': 333,
    '.': 278,
    '/': 278,
    ':': 278,
    ';': 278,
    '<': 584,
    '=': 584,
    '>': 584,
    '?': 556,
    '@': 1015,
    A: 667,
    B: 667,
    C: 722,
    D: 722,
    E: 667,
    F: 611,
    G: 778,
    H: 722,
    I: 278,
    J: 500,
    K: 667,
    L: 556,
    M: 833,
    N: 722,
    O: 778,
    P: 667,
    Q: 778,
    R: 722,
    S: 667,
    T: 611,
    U: 722,
    V: 667,
    W: 944,
    X: 667,
    Y: 667,
    Z: 611,
    '[': 278,
    '\\': 278,
    ']': 278,
    '^': 469,
    _: 556,
    '`': 333,
    a: 556,
    b: 556,
    c: 500,
    d: 556,
    e: 556,
    f: 278,
    g: 556,
    h: 556,
    i: 222,
    j: 222,
    k: 500,
    l: 222,
    m: 833,
    n: 556,
    o: 556,
    p: 556,
    q: 556,
    r: 333,
    s: 500,
    t: 278,
    u: 556,
    v: 500,
    w: 722,
    x: 500,
    y: 500,
    z: 500,
    '{': 334,
    '|': 260,
    '}': 334,
    '~': 584,
    Ä: 667,
    Ö: 778,
    Ü: 722,
    ä: 556,
    ö: 556,
    ü: 556,
    ß: 611,
    '°': 400,
    '—': 1000,
    '–': 556,
    '„': 333,
    '“': 333,
    '”': 333,
    '‚': 222,
    '‘': 222,
    '’': 222,
    '…': 1000,
    '×': 584
};

/** Digits share one width; anything unlisted counts as wide as a digit. */
const DEFAULT_WIDTH = 556;

/**
 * react-pdf breaks lines its own way (and hyphenates), so the greedy count
 * here is held a little narrower than the box to err towards one line more.
 */
const SAFETY = 0.95;

function textWidth(text: string, fontSize: number): number {
    let total = 0;
    for (const character of text) {
        total += HELVETICA_WIDTHS[character] ?? DEFAULT_WIDTH;
    }
    return (total * fontSize) / 1000;
}

/**
 * The lines `text` wraps to in a box `width` wide, word by word; a word longer
 * than the box is broken where it overflows. Never less than one line.
 */
export function captionLineCount(
    text: string,
    width: number,
    fontSize: number
): number {
    const room = width * SAFETY;
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0 || room <= 0) {
        return 1;
    }
    const space = textWidth(' ', fontSize);
    let lines = 1;
    let used = 0;
    for (const word of words) {
        const wordWidth = textWidth(word, fontSize);
        const needed = used === 0 ? wordWidth : used + space + wordWidth;
        if (needed <= room) {
            used = needed;
            continue;
        }
        if (used > 0) {
            lines += 1;
        }
        // A word wider than the box fills whole lines of its own.
        const overflowLines = Math.max(0, Math.ceil(wordWidth / room) - 1);
        lines += overflowLines;
        used = wordWidth - overflowLines * room;
    }
    return lines;
}
